import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H3 from '@base/H3/H3';
import H1 from '@base/H1/H1';
import P1 from '@base/P1/P1';
import NImage from '@base/Image';
import Button from '@base/Button/Button';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import { getImage } from '@utils/imageHelper';
import AppInstallBanner from '@comp/AppInstallBanner/Version1';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import Head from 'next/head';
import pushRoute from '@utils/pushRoute';
import { breakpoints, Fonts, Spacings } from '@styles/index';
import {
  SPACING_14B,
  SPACING_22B,
  SPACING_4B,
  SPACING_6,
  SPACING_6B,
  SPACING_20B,
  SPACING_8B,
  SPACING_1B
} from '@styles/constants/spacings';
import { segment } from '@actions/analytics';
import useWindowSize from '@hooks/useWindowSize';
import { CHECKOUT_OFFER_VIEWED } from '@constants/segment';
import GoalBenefits from './GoalBenefits';
import usePageHead from './usePageHead';

const discountIcon = 'discount-green.svg';
const OuterWrapper = styled.div`
  background-repeat: no-repeat;
  background-image: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(229, 244, 255, 0.44) 100%
  );
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1366px;
  margin: auto;
  position: relative;
  height: 640px;
  text-align: center;
  padding-top: 170px;
  @media (max-width: ${breakpoints.tablet}) {
    height: 452px;
    padding-top: ${SPACING_22B};
    max-width: 876px;
  }
  @media (max-width: ${breakpoints.mobile}) {
    padding: ${SPACING_14B} ${SPACING_4B} 0;
    height: 393px;
    text-align: left;
  }
`;

const WrapperWithBg = styled(Wrapper)`
  &:before,
  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    background-repeat: no-repeat;
  }
  &:after {
    background-image: url('${getImage('goal/k12/lady.svg')}');
    background-position: bottom right;
    @media (max-width: ${breakpoints.mobile}) {
      background-size: 280px auto;
      background-position: bottom right -58px;
    }
  }
  &:before {
    background-image: url('${getImage('goal/k12/wall.svg')}');
    background-position: center right 240px;
    @media (max-width: ${breakpoints.mobile}) {
      z-index: -1;
      opacity: 0.5;
      background-size: 400px auto;
      background-position: bottom 56px right 16px;
    }
  }
`;

const InnerWrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  text-align: left;
  position: relative;
  z-index: 1;
  @media (max-width: ${breakpoints.tablet}) {
    max-width: 728px;
  }
`;

const Header = styled.div`
  margin: ${SPACING_6B} auto ${SPACING_6} auto;
  @media (max-width: ${breakpoints.tablet}) {
    margin-bottom: ${SPACING_4B};
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin: 0;
    text-align: left;
  }
`;

const SubHeaderContainer = styled.div`
  margin: 0px 0px 100px 0px;
  width: 61.62%;
  @media (max-width: ${breakpoints.tablet}) {
    width: 50%;
    margin-bottom: ${SPACING_20B};
  }
  @media (max-width: ${breakpoints.mobile}) {
    width: 280px;
    display: flex;
    align-items: center;
    height: 64px;
    margin: 0;
    margin-top: ${SPACING_1B};
    margin-bottom: ${SPACING_4B};
  }
`;

const SubHeader = styled(H3)`
  font-weight: ${Fonts.REGULAR};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  @media (max-width: ${breakpoints.tablet}) {
    font-size: ${Fonts.H5};
  }
  @media (max-width: ${breakpoints.mobile}) {
    font-size: ${Fonts.H6};
  }
`;

const SubButton = styled(Button)`
  margin: auto;
  width: fit-content;
  min-height: 0;
  height: auto;
  font-size: ${Fonts.H4};
  display: inline-block;
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  padding: 18px ${SPACING_8B};
  @media (max-width: ${breakpoints.tablet}) {
    font-size: ${Fonts.H5};
    line-height: ${Fonts.BUTTON_LINE_HEIGHT};
    padding: ${SPACING_4B} ${SPACING_6B};
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin: 0;
    font-size: ${Fonts.H6};
    padding: ${SPACING_4B} 30px;
    min-width: 169px;
  }
`;

const Title = styled(H1)`
  @media (max-width: ${breakpoints.tablet}) {
    font-size: ${Fonts.H2};
    line-height: ${Fonts.PARA_LINE_HEIGHT};
  }
  @media (max-width: ${breakpoints.mobile}) {
    width: 220px;
  }
`;

const OfferWrap = styled.div`
  margin-top: ${Spacings.SPACING_8B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: ${Spacings.SPACING_6B};
  }
`;

const OfferCTACont = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_3B};
  align-items: center;
`;

const OfferIconWrap = styled.div`
  margin-right: ${Spacings.SPACING_1B};
`;

const StyledUnderlineLink = styled(UnderlineLink)`
  padding-bottom: ${Spacings.SPACING_2};
`;

const LinkText = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const K12PageHead = ({
  goalUID,
  goalSlug,
  goalDetails,
  isSubscribed,
  block
}) => {
  const offerHeader = block.extraBlockInfo?.offerHeader;
  const { handleSubscriptionClick, isDrawerOpen, closeDrawer } = usePageHead({
    goalSlug,
    goalUID,
    goalDetails,
    isSubscribed
  });
  const { name } = goalDetails;
  const purchaseCTALabel = usePurchaseCTAHandler(goalUID);
  const CTALabel = isSubscribed ? 'Download App' : purchaseCTALabel;
  const { isMobile } = useWindowSize();
  const title = `Prepare for ${name}`;
  const description =
    'Learn with the best classroom experience on India’s largest learning platform';
  return (
    <>
      <Head>
        <link rel="preload" as="image" href={getImage('goal/k12/lady.svg')} />
        <link rel="preload" as="image" href={getImage('goal/k12/wall.svg')} />
      </Head>
      <OuterWrapper>
        <WrapperWithBg>
          <InnerWrapper>
            <Header>
              <Title>{title}</Title>
            </Header>
            <SubHeaderContainer>
              <SubHeader>{description}</SubHeader>
            </SubHeaderContainer>
            <SubButton
              label={CTALabel}
              variant="filled"
              onClick={handleSubscriptionClick}
              dataAnalytics="goal-getSubscription"
            />
            {offerHeader && (
              <OfferWrap>
                <OfferCTACont>
                  <OfferIconWrap>
                    <NImage
                      width="24px"
                      height="24px"
                      src={discountIcon}
                      alt="discount"
                    />
                  </OfferIconWrap>
                  <StyledUnderlineLink
                    color="var(--color-i-green)"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      segment.track(CHECKOUT_OFFER_VIEWED, {
                        goal_name: name,
                        goal_uid: goalUID,
                        is_combo: false
                      });
                      pushRoute(
                        '/goal/[goalSlug]/[goalUID]/offers',
                        `/goal/${goalSlug}/${goalUID}/offers`,
                        {},
                        'goal'
                      );
                    }}
                  >
                    <LinkText color="var(--color-i-green)">
                      View subscription offers
                    </LinkText>
                  </StyledUnderlineLink>
                </OfferCTACont>
                {!isMobile && (
                  <P1 color="var(--color-text-primary)">{offerHeader}</P1>
                )}
              </OfferWrap>
            )}
          </InnerWrapper>
        </WrapperWithBg>
      </OuterWrapper>
      <GoalBenefits goalUID={goalUID} goalSlug={goalSlug} />
      <AppInstallBanner onClose={closeDrawer} open={isDrawerOpen} />
    </>
  );
};

export default K12PageHead;

K12PageHead.propTypes = {
  goalUID: PropTypes.string,
  goalSlug: PropTypes.string,
  goalDetails: PropTypes.objectOf(PropTypes.any),
  isSubscribed: PropTypes.bool
};

K12PageHead.defaultProps = {
  goalUID: '',
  goalSlug: '',
  goalDetails: {},
  isSubscribed: false
};
